import React, { FC } from 'react';
import styled from '@emotion/styled';

import PencilIcon from '~Assets/icons/edit.svg';
import Icon from '~ComponentsImproved/Icon';
import MessageInput from '~ComponentsImproved/MessageInput';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useButton, useI18n } from '~Hooks';
import { Box, TransparentButton } from '~Styles';

const Wrapper = styled.div<{ fullWidth: boolean }>`
  display: inline-block;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  box-sizing: border-box;
`;

const Item = styled.div<{
  disabled: boolean;
  darkBackground: boolean;
}>`
  display: flex;
  align-items: flex-start;
  position: relative;
  justify-content: space-between;
  background-color: ${({ darkBackground }) =>
    darkBackground ? 'var(--colorBlueGray1100)' : 'transparent'};
  padding: ${({ darkBackground }) => (darkBackground ? '12px 16px' : '0')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
  flex: 1;
  min-width: 0;
`;

const EditButton = styled(TransparentButton)`
  padding: 0;
  text-transform: none;
  flex-shrink: 0;
  white-space: nowrap;
`;

export interface Props {
  label?: string;
  description?: string | JSX.Element;
  value: number | '';
  /** Formatted value shown on the button, e.g. "30 min". Defaults to the raw value. */
  displayValue?: string;
  /**
   * Called with the submitted value once the user confirms the input. Return
   * `false` to keep the input open, e.g. when the value was rejected by the
   * caller's validation; anything else closes it.
   */
  onSave?: (value: number | '') => boolean | void;
  disabled?: boolean;
  lightText?: boolean;
  darkBackground?: boolean;
  fullWidth?: boolean;
  /** If provided, this component is rendered in Settings and we need to check if it's remotely controlled */
  settingKey?: string;
}

/**
 * Settings row for a numeric value. The value doubles as the edit button and
 * opens the shared MessageInput, which is the input we already rely on across
 * handhelds, Adyen devices, phones, ELO screens and Android tablets.
 */
const NumberSetting: FC<Props> = ({
  label,
  description,
  value,
  displayValue,
  onSave,
  disabled = false,
  lightText = false,
  darkBackground = false,
  fullWidth = false,
  settingKey,
}) => {
  const { i18n } = useI18n();
  const deviceHandler = useDeviceHandler();
  const isRemotelyControlled = settingKey && deviceHandler.remotelyControlledData[settingKey];
  const inputModal = useModal();

  const textColor = lightText || darkBackground ? 'var(--colorWhite)' : 'var(--colorBlack)';
  const descriptionTextColor =
    lightText || darkBackground ? 'var(--colorBlueGray300)' : 'var(--colorBlack)';

  const isDisabled = disabled || Boolean(isRemotelyControlled);

  const editButton = useButton({
    onClick: inputModal.show,
    disabled: isDisabled,
  });

  /** Hands the typed value to the caller in the `number | ''` shape it validates against. */
  const handleSubmit = (message: string) => {
    const trimmed = message.trim();
    const submitted = trimmed === '' ? '' : Number(trimmed);

    // The caller rejected the value, so keep the input open with what was typed.
    if (onSave?.(submitted) === false) {
      return;
    }

    inputModal.hide();
  };

  return (
    <Wrapper fullWidth={fullWidth}>
      <Item disabled={isDisabled} darkBackground={darkBackground}>
        {label && (
          <Text>
            <Heading level={5} color={textColor}>
              {label}
            </Heading>

            {description && (
              <Paragraph level={5} color={descriptionTextColor}>
                {description}
              </Paragraph>
            )}
          </Text>
        )}
        <Box display="flex" alignItems="center" gap="xs">
          {Boolean(isRemotelyControlled) && (
            <Paragraph level={5} color={descriptionTextColor}>
              ({i18n('Settings.LockedByBaemingo')})
            </Paragraph>
          )}
          <EditButton display="flex" alignItems="center" gap="xs" {...editButton}>
            <Heading level={5} color={textColor}>
              {displayValue ?? value}
            </Heading>

            <Icon src={PencilIcon} color="var(--colorBlueGray900)" width="20px" height="20px" />
          </EditButton>
        </Box>
      </Item>

      <Modal isShowing={inputModal.isShowing} hide={inputModal.hide}>
        <MessageInput
          type="text"
          // Numeric keypad on touch devices while the input itself stays a text
          // field, so partially typed values are never swallowed by the browser.
          inputMode="numeric"
          autoFocus
          value={value === '' ? '' : String(value)}
          onSubmit={handleSubmit}
        />
      </Modal>
    </Wrapper>
  );
};

export default React.memo(NumberSetting);
